import { useTranslate, useSelect } from '@pankod/refine-core'
import { Alert, Form, Modal, ModalProps, Progress, Select } from '@pankod/refine-antd'
import { ADMITTED_STATUS } from 'utility/constants'
import { definitions } from 'interfaces'
import { supabaseClient } from 'utility'
import { useEffect, useRef, useState } from 'react'
import { BulkItem, BulkResult, runBulkStatus } from './bulkStatus'

type Props = {
  modalProps: ModalProps
  close: any
  tableQuery: any
  selectedRows: definitions['shipments'][]
}
type Job = { items: BulkItem[]; target: number; results: BulkResult[] }

export const ModalChangeOrdersStatus: React.FC<Props> = ({ modalProps, close, tableQuery, selectedRows }) => {
  const t = useTranslate()
  const [statusId, setStatusId] = useState<number>()
  const [loading, setLoading] = useState(false)
  const [job, setJob] = useState<Job>()
  const [error, setError] = useState('')
  const running = useRef(false)
  const user = supabaseClient.auth.user()
  const storageKey = `shipment-status-job-v1:${user?.id || 'no-session'}`
  const { options } = useSelect({
    resource: 'status',
    fetchSize: 20,
    optionValue: 'id',
    optionLabel: 'name',
    filters: [{ field: 'id', operator: 'gte', value: ADMITTED_STATUS }],
    sort: [{ field: 'id', order: 'asc' }]
  })

  useEffect(() => {
    if (!modalProps.visible || running.current) return
    setError('')
    try {
      const saved = localStorage.getItem(storageKey)
      if (!saved) { setJob(undefined); return }
      const parsed: Job = JSON.parse(saved)
      if (!Array.isArray(parsed.items) || !Array.isArray(parsed.results) || !Number.isInteger(parsed.target) ||
        !parsed.items.every(item => Number.isInteger(item.id) && Number.isInteger(item.expectedStatus))) {
        throw new Error('invalid checkpoint')
      }
      setJob(parsed)
      setStatusId(parsed.target)
    } catch (_) {
      setError('No se puede recuperar el lote guardado. No se iniciará otro lote hasta revisar el registro local.')
    }
  }, [modalProps.visible, storageKey])

  useEffect(() => {
    if (!loading) return
    const preventClose = (event: BeforeUnloadEvent) => { event.preventDefault(); event.returnValue = '' }
    window.addEventListener('beforeunload', preventClose)
    return () => window.removeEventListener('beforeunload', preventClose)
  }, [loading])

  const confirmed = job?.results.filter(result => result.state === 'confirmed').length || 0
  const complete = !!job && confirmed === job.items.length
  const pending = job?.results.filter(result => result.state !== 'confirmed') || []

  const updateOrdersStatus = async () => {
    if (running.current) return
    if (complete) { close(); return }
    const operator = supabaseClient.auth.user()
    if (!operator || !Number.isInteger(job?.target ?? statusId)) return
    const items: BulkItem[] = Array.from(new Map(selectedRows.map((row: any) => [row.order_id, {
      id: row.order_id, expectedStatus: row.order?.status_id, prevStatus: row.order?.prev_status_id ?? null
    }])).values())
    const active: Job = job || {
      items, target: statusId!, results: items.map(item => ({ id: item.id, state: 'pending', attempts: 0 }))
    }
    if (!active.items.length || !active.items.every(item => Number.isInteger(item.id) && Number.isInteger(item.expectedStatus))) {
      setError('Selecciona pedidos con un estado válido antes de continuar.'); return
    }
    running.current = true
    setLoading(true)
    setError('')
    try {
      // Save the exact selection BEFORE any request, including on first execution.
      localStorage.setItem(storageKey, JSON.stringify(active))
      setJob(active)
      const results = await runBulkStatus(active.items, active.target, {
        read: async (id) => {
          if (supabaseClient.auth.user()?.id !== operator.id) throw new Error('session changed')
          const response = await supabaseClient.from('orders').select('id,status_id').eq('id', id).single()
          if (response.error) throw response.error
          return response.data?.status_id ?? null
        },
        write: async (item, target) => {
          if (supabaseClient.auth.user()?.id !== operator.id) throw new Error('session changed')
          const response = await supabaseClient.from('orders').update({
            status_id: target, prev_status_id: item.prevStatus, updated_by: operator.email
          }, { returning: 'minimal' }).eq('id', item.id).eq('status_id', item.expectedStatus)
          if (response.error) throw response.error
        },
        save: (results) => {
          const checkpoint = { ...active, results }
          localStorage.setItem(storageKey, JSON.stringify(checkpoint))
          setJob(checkpoint)
        }
      })
      setJob({ ...active, results })
      if (results.every(result => result.state === 'confirmed')) localStorage.removeItem(storageKey)
      await tableQuery.refetch()
    } catch (_) {
      setError('El lote no está confirmado como completo. Conserva esta ventana y reanuda para comprobar los estados reales.')
    } finally {
      running.current = false
      setLoading(false)
    }
  }

  return <Modal {...modalProps} title={t('actions.changeOrdersStatus')} centered
    onOk={updateOrdersStatus} onCancel={close} confirmLoading={loading}
    closable={!loading} maskClosable={!loading} keyboard={!loading} cancelButtonProps={{ disabled: loading }}
    okText={complete ? 'Cerrar' : job ? 'Comprobar y continuar lote' : 'Procesar todos los seleccionados'}
    okButtonProps={{ disabled: loading || (!job && (!selectedRows.length || statusId === undefined)) || error.includes('registro local') }}>
    <p>{job ? job.items.length : new Set(selectedRows.map(row => row.order_id)).size} pedidos en el lote.</p>
    <Form layout="vertical"><Form.Item label={t('order.fields.status')}>
      <Select options={options} value={job?.target ?? statusId} onChange={setStatusId} disabled={loading || !!job} />
    </Form.Item></Form>
    {job && <>
      <Progress percent={Math.floor(confirmed * 100 / job.items.length)} status={complete ? 'success' : 'active'} />
      <p>{confirmed} de {job.items.length} pedidos confirmados en el estado solicitado.</p>
      {!loading && !complete && <Alert type="warning" message="Lote pendiente: no se ha dado por completado."
        description={`Pendientes de comprobar o resolver: ${pending.map(result => result.id).join(', ')}. Se comprobará cada estado antes de reintentar; los cambios de estado ajenos no se sobrescribirán.`} />}
      {complete && <Alert type="success" message="Todos los pedidos del lote están confirmados." />}
    </>}
    {loading && <Alert type="info" message="Mantén esta pestaña abierta. Si se interrumpe, podrás recuperar el lote desde este mismo navegador y usuario." />}
    {error && <Alert type="error" message={error} />}
  </Modal>
}
